import React, { memo, useEffect, useState } from 'react';
import { T } from '../../utils/I18n/I18n';
import clsx from 'clsx';
import { Link, Navigate, useLocation } from 'react-router-dom';
import productionConfiguration from '../../config/default.json';
import CheckoutCheckmark from '../../assets/icons/checkout-checkmark.svg';
import HeaderCartImage from '../../assets/icons/header-icon-mini-cart.svg';
import { CartConsumer, useCartContext } from '../../context/CartContext';
import { useUserContext } from '../../context/UserContext';
import useResponsive from '../../hooks/useResponsiveV2';
import readConfig from '../../utils/readClientConfiguration';
import './Checkout.scss';
import { usePopup } from '../../context/PopupContext';
import CheckoutCartOverlay from './CheckoutCartOverlay';
import CheckoutStep1Form from './CheckoutStep1Form';
import CheckoutStep2Form from './CheckoutStep2Form';
import CustomerService from "./CustomerService/CustomerService";
import { isSI } from '../../utils/isHr';

const phoneSupportNumber = readConfig(productionConfiguration, 'phoneSupportNumber');

const CheckoutHeaderUser = memo(() => {
    const { user } = useUserContext();

    return (
        <div className="checkout__header__status ">
            {user ? (
                <>
                    {<T id="checkout.loggedOnAs" />}{' '}
                    <Link to="/customer/login">
                        {user.firstname} {user.lastname}
                    </Link>
                </>
            ) : (
                <>
                    {<T id="checkout.buyAsAGuest" />}{' '}
                    <Link to="/customer/login">
                        <T id="product.here" />
                    </Link>
                </>
            )}
        </div>
    );
});

const CheckoutHeader = () => {
    return (
        <header className="checkout__header">
            <Link to="/">
                <div className="checkout__header__image">
                    <img src="/images/checkout/emmezeta-blagajna-header.png" alt="blagajna" />
                </div>
            </Link>

            <CheckoutHeaderUser />
        </header>
    );
};

const CheckoutDesktopHeader = () => {
    return (
        <header className="checkout__header">
            <div className="checkout__header__wrapper">
                <Link to="/">
                    <div className="checkout__header__image">
                        <img src="/images/checkout/emmezeta-blagajna-header.png" alt="blagajna" />
                    </div>
                </Link>

                <CheckoutHeaderUser />

                <div style={{ flexGrow: 1 }} />

                <CustomerService />
            </div>
        </header>
    );
};

const CheckoutFooter = () => (
    <footer className="checkout__footer">
        <div className="checkout__footer__support">
            <div className="checkout__footer__support__question">
                <T id="cart.needHelp" />
            </div>
            <div className="checkout__footer__support__phone">
                <a href={`tel:${phoneSupportNumber}`}>{phoneSupportNumber}</a>
            </div>
        </div>

        <div className="checkout__footer__info">
            <T id="checkout.customerServiceText" />
        </div>
    </footer>
);

const CheckoutPaymentInfo = () => {
    return (
        <div className="checkout__payment-info">
            <div className="checkout__payment-info__reservation">
                <T id="checkout.timeReservationText" />
            </div>

            <div className="checkout__payment-info__card-icons">
                {!isSI && <img src="/images/icon-cc-diners.png" alt="diners" />}
                <img src="/images/icon-cc-maestro.png" alt="maestro" />
                <img src="/images/icon-cc-mastercard.png" alt="mastercard" />
                <img src="/images/icon-cc-visa.png" alt="visa" />
            </div>

            <div className="checkout__payment-info__monri-secure">
                <img src="/images/icon-webpay-by-monri.png" alt="monri" />

                <div className="monri-secure-notice">
                    <T id="checkout.dataSecrecyText" />
                </div>
            </div>
        </div>
    );
};

const Checkout = () => {
    const [redirect, setRedirect] = useState(false);
    const { cart } = useCartContext();
    const { showAddToCartPopupMessage } = usePopup();
    const { isDesktop } = useResponsive();
    const [checkoutStep, setCheckoutStep] = useState(1);
    const [overlayHidden, setOverlayHidden] = useState(true); // Dodano stan dla overlay

    const location = useLocation();

    useEffect(() => {
        if (location.state) {
            if (location.state.errorMessage) {
                showAddToCartPopupMessage(location.state.errorMessage, 'error', 5000);
            }
        }
    }, [location.state, showAddToCartPopupMessage]);

    useEffect(() => {
        if (cart) {
            if (cart.items.length === 0) {
                setRedirect(true);
            }
        }
    }, [cart]);

    if (redirect) {
        return <Navigate to="/checkout/cart" replace />;
    }

    const step1Classes = clsx('checkout-step checkout-step--1', checkoutStep === 2 && 'checkout-step--completed');
    const step2Classes = clsx('checkout-step checkout-step--2', checkoutStep === 2 && 'checkout-step--active');

    return (
        <div className="checkout">
            {isDesktop ? <CheckoutDesktopHeader /> : <CheckoutHeader />}

            <div className="checkout__checkout-steps">
                <Link to="#" className={step1Classes} onClick={() => setCheckoutStep(1)}>
                    <div className="number-circle">
                        {checkoutStep === 1 ? 1 : <img src={CheckoutCheckmark} alt="checkmark" />}
                    </div>

                    <div className="checkout-step__text">
                        <T id="orders.orderMethod" />
                    </div>
                </Link>

                <Link
                    to="#"
                    className={step2Classes}
                    onClick={() => {
                        if (cart && cart.shipping_addresses && cart.shipping_addresses.length) {
                            setCheckoutStep(2);
                        }
                    }}
                >
                    <div className="number-circle">2</div>

                    <div className="checkout-step__text">
                        <T id="orders.paymentMethodLower" />
                    </div>
                </Link>
            </div>

            {!isDesktop && (
                <div className="checkout__cart">
                    <CartConsumer>
                        {({ cart }) => {
                            const totalQuantity = (cart && cart.total_quantity) || 0;
                            const grandTotal = (cart && cart.prices.grand_total.value) || 0;
                            const currency = cart && cart.items.length && cart.items[0].product.prices[0].currency_symbol;

                            return (
                                <>
                                    <div className="checkout__cart__price">
                                        <div className="checkout__cart__price__text">
                                            <T id="headerCart.total" />
                                        </div>

                                        <div className="checkout__cart__price__amount">
                                            {grandTotal.toFixed(2)} {currency}
                                        </div>
                                    </div>

                                    <div className="checkout__cart__icon">
                                        <Link to="#" onClick={() => setOverlayHidden(false)}>
                                            <img className="pointer" src={HeaderCartImage} alt="cart" />
                                        </Link>

                                        <div className="number-circle">{totalQuantity}</div>
                                    </div>
                                </>
                            );
                        }}
                    </CartConsumer>
                </div>
            )}

            {cart && !isDesktop && (
                <>
                    {checkoutStep === 1 && <CheckoutStep1Form onContinue={() => setCheckoutStep(2)} />}
                    {checkoutStep === 2 && <CheckoutStep2Form setCheckoutStep={setCheckoutStep} />}
                    {!overlayHidden && (
                        <CheckoutCartOverlay
                            onEditClick={() => setCheckoutStep(1)}
                            checkoutStep={checkoutStep}
                            onClose={() => setOverlayHidden(true)} // Dodano możliwość zamknięcia overlay
                        />
                    )}
                </>
            )}

            {cart && isDesktop && (
                <div className="checkout__desktop">
                    <div className="checkout__desktop__main">
                        {checkoutStep === 1 && <CheckoutStep1Form onContinue={() => setCheckoutStep(2)} />}
                        {checkoutStep === 2 && <CheckoutStep2Form setCheckoutStep={setCheckoutStep} />}
                    </div>

                    <div className="checkout__desktop__aside">
                        <CheckoutCartOverlay onEditClick={() => setCheckoutStep(1)} checkoutStep={checkoutStep} />
                        <CheckoutPaymentInfo />
                    </div>
                </div>
            )}

            {cart && !isDesktop && <CheckoutPaymentInfo />}

            {!isDesktop && <CheckoutFooter />}
        </div>
    );
};

export default Checkout;